import { create } from 'zustand';

interface Attachment {
  id: number;
  url: string;
  content_type: string;
  filename: string;
  byte_size?: number;
}

interface OfferStore {
  applicationId: number;
  offerId: number;
  details: any;
  templates: any;
  offerAttachments: Attachment[];
  isPolling: boolean;
  applicationData: any;
  signatureType: 'kula' | 'docusign' | 'pandadoc' | 'adobe';
  isApprovalEnabled: boolean;
  isReapprovalFieldsModified: boolean;
  skipApproval: boolean; // skip approval is a combination of isApprovalEnabled and isReapprovalFieldsModified
  currentOffer: any;
  hasValidTokens: boolean;
  enableNextButton: boolean;
  offerPayload: any;
  // True when the selected template renders the multi-doc panel (which carries its
  // own "Add more files" button), so the footer button can hide to avoid duplication.
  isMultiDocTemplate: boolean;
  setIsMultiDocTemplate: (isMultiDocTemplate: boolean) => void;
  setHasValidTokens: (hasValidTokens: boolean) => void;
  setCurrentOffer: (currentOffer: any) => void;
  setSkipApproval: (skipApproval: boolean) => void;
  setIsReapprovalFieldsModified: (isReapprovalFieldsModified: boolean) => void;
  setApplicationId: (applicationId: number) => void;
  setApplicationData: (applicationData: any) => void;
  setOfferId: (offerId: number) => void;
  setDetails: (details: any) => void;
  setTemplates: (templates: any) => void;
  setOfferAttachments: (attachments: Attachment[]) => void;
  addOfferAttachment: (attachment: Attachment) => void;
  removeOfferAttachment: (attachmentId: number) => void;
  setSignatureType: (
    signatureType: 'kula' | 'docusign' | 'pandadoc' | 'adobe'
  ) => void;
  setIsPolling: (isPolling: boolean) => void;
  setIsApprovalEnabled: (isApprovalEnabled: boolean) => void;
  setEnableNextButton: (enableNextButton: boolean) => void;
  setOfferPayload: (offerPayload: any) => void;
}

export const useOfferStore = create<OfferStore>((set) => ({
  applicationId: 0,
  offerId: 0,
  details: {},
  templates: {},
  offerAttachments: [],
  isPolling: false,
  applicationData: {},
  signatureType: 'kula',
  isApprovalEnabled: false,
  isReapprovalFieldsModified: false,
  skipApproval: false,
  currentOffer: {},
  hasValidTokens: true,
  enableNextButton: false,
  offerPayload: null,
  isMultiDocTemplate: false,
  setIsMultiDocTemplate: (isMultiDocTemplate: boolean) =>
    set({ isMultiDocTemplate }),
  setOfferPayload: (offerPayload: any) => set({ offerPayload }),
  setHasValidTokens: (hasValidTokens: boolean) => set({ hasValidTokens }),
  setCurrentOffer: (currentOffer: any) => set({ currentOffer }),
  setSkipApproval: (skipApproval: boolean) => set({ skipApproval }),
  setIsReapprovalFieldsModified: (isReapprovalFieldsModified: boolean) =>
    set({ isReapprovalFieldsModified }),
  setIsApprovalEnabled: (isApprovalEnabled: boolean) =>
    set({ isApprovalEnabled }),
  setApplicationId: (applicationId: number) => set({ applicationId }),
  setApplicationData: (applicationData: any) => set({ applicationData }),
  setOfferId: (offerId: number) => set({ offerId }),
  setDetails: (details: any) => set({ details }),
  setTemplates: (templates: any) => set({ templates }),
  setOfferAttachments: (attachments: Attachment[]) =>
    set({ offerAttachments: attachments }),
  addOfferAttachment: (attachment: Attachment) =>
    set((state) => ({
      offerAttachments: [...state.offerAttachments, attachment],
    })),
  removeOfferAttachment: (attachmentId: number) =>
    set((state) => ({
      offerAttachments: state.offerAttachments.filter(
        (a) => a.id !== attachmentId
      ),
    })),
  setSignatureType: (
    signatureType: 'kula' | 'docusign' | 'pandadoc' | 'adobe'
  ) => set({ signatureType }),
  setIsPolling: (isPolling: boolean) => set({ isPolling }),
  setEnableNextButton: (enableNextButton: boolean) => set({ enableNextButton }),
}));
